/**
 * React hook for contact form operations
 */

import { useState, useCallback } from 'react';
import contactService, { ContactFormSubmissionResult } from '../services/ContactService';
import { ContactFormData } from '../services/ApiService';

interface UseContactFormReturn {
  isSubmitting: boolean;
  isSuccess: boolean;
  error: string | null;
  validationErrors: Array<{ field: string; message: string }>;
  submitForm: (data: ContactFormData) => Promise<void>;
  resetForm: () => void;
  successMessage: string | null;
  requestId: string | null;
}

export const useContactForm = (): UseContactFormReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Array<{ field: string; message: string }>>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);

  const submitForm = useCallback(async (data: ContactFormData): Promise<void> => {
    // Reset previous state
    setIsSubmitting(true);
    setError(null);
    setValidationErrors([]);
    setIsSuccess(false);
    setSuccessMessage(null);
    setRequestId(null);

    try {
      // Client-side validation first
      const clientValidationErrors = contactService.validateFormData(data);
      if (clientValidationErrors.length > 0) {
        setValidationErrors(clientValidationErrors);
        setError('Please correct the errors below');
        return;
      }

      // Submit to API
      const result: ContactFormSubmissionResult = await contactService.submitForm(data);

      if (result.success) {
        setIsSuccess(true);
        setSuccessMessage(
          contactService.formatSuccessMessage(result.message, result.requestId)
        );
        setRequestId(result.requestId || null);
      } else {
        setError(result.message);
        setValidationErrors(result.errors || []);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Contact form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const resetForm = useCallback(() => {
    setIsSubmitting(false);
    setIsSuccess(false);
    setError(null);
    setValidationErrors([]);
    setSuccessMessage(null);
    setRequestId(null);
  }, []);

  return {
    isSubmitting,
    isSuccess,
    error,
    validationErrors,
    submitForm,
    resetForm,
    successMessage,
    requestId,
  };
};

/**
 * Hook for getting validation error for a specific field
 */
export const useFieldError = (
  validationErrors: Array<{ field: string; message: string }>,
  fieldName: string
): string | null => {
  const fieldError = validationErrors.find(error => error.field === fieldName);
  return fieldError?.message || null;
};